import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import phone from '@iconify-icons/mdi/phone';
import { BREAKPOINTS } from '../../constants';
import { TRANSLATIONS } from '../../translations';
import Drawer from '../ui-components/Drawer/Drawer';
import BetaIcon, { IconColors, IconSizes } from './Icons/Icon';
import { useMatchesMinWidth } from '../hooks/useMatchesMinWidth';
import Button, { ButtonSizes, ButtonStyles } from './Button/Button';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../ui-components/Typography/Typography';
import {
  trackRankedPhoneButtonClicked,
  normalizePhoneSecureValue,
} from '../utils/posthogTracking';

const DEFAULT_START_TIME = '09:00:00';
const DEFAULT_END_TIME = '20:00:00';

const CucaSupportButton = ({
  phoneLink,
  listingPhoneSecureValue,
  phoneSecureValue = 0,
  startTime = DEFAULT_START_TIME,
  endTime = DEFAULT_END_TIME,
  isPhoneActive,
}) => {
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(
    BREAKPOINTS.TABLET_LANDSCAPE
  );
  const [cucaIsAvailable, setCucaIsAvailable] = useState(null);
  const [hideCucaButton, setHideCucaButton] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [assistanceMessage, setAssistanceMessage] = useState('');
  const [isWithinTime, setIsWithinTime] = useState(false);
  const normalizedListingPhoneSecureValue = normalizePhoneSecureValue(
    listingPhoneSecureValue
  );
  const hasListingPhoneSecureValue = normalizedListingPhoneSecureValue !== null;

  const isWithinBusinessHours = (fromTime, toTime) => {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    const [fromHours, fromMinutes] = fromTime.split(':').map(Number);
    const [toHours, toMinutes] = toTime.split(':').map(Number);

    const fromTotal = fromHours * 60 + fromMinutes;
    const toTotal = toHours * 60 + toMinutes;

    return nowMinutes >= fromTotal && nowMinutes <= toTotal;
  };

  const offLineDrawer = useMemo(
    () => (
      <Drawer
        classNames={'drawer--content-fit'}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={app_main_wpml.telephone_assistance}
        body={
          <div className="d-flex flex-column pt-xl pb-xl">
            <div className="m-auto mb-m">
              <BetaIcon
                name={'headset'}
                color={IconColors.regular}
                size={IconSizes[64]}
              />
            </div>
            {isWithinTime ? (
              <div className="d-flex flex-column">
                <div
                  className="text-center pl-l pr-l pb-s typography__p2 typography__weight--bold typography__neutral"
                  dangerouslySetInnerHTML={{
                    __html:
                      TRANSLATIONS.TELEPHONE_SUPPORT_IS_CURRENTLY_UNAVAILABLE,
                  }}
                />
                <div
                  className="text-center pl-l pr-l pb-s typography__p2 typography__neutral"
                  dangerouslySetInnerHTML={{
                    __html: TRANSLATIONS.NEED_HELP_OPEN_A_CHAT,
                  }}
                />
              </div>
            ) : (
              <div className="d-flex flex-column">
                <div
                  className="text-center pl-l pr-l pb-s typography__p2 typography__weight--bold typography__neutral"
                  dangerouslySetInnerHTML={{
                    __html: TRANSLATIONS.WE_ARE_OUT_AT_THE_MOMENT,
                  }}
                />
                <Typography
                  classNames="text-center pl-l pr-l pb-s"
                  variant={TypographyVariants.p2}
                >
                  {assistanceMessage}
                </Typography>
                <Typography
                  classNames="text-center pl-l pr-l"
                  variant={TypographyVariants.p2}
                >
                  {TRANSLATIONS.CONTACT_US_VIA_CHAT_WE_WILL_ANSWER_YOU}
                </Typography>
              </div>
            )}
            <div className="m-auto mt-m">
              <Button
                style={ButtonStyles.secondary}
                size={ButtonSizes.m}
                onClick={() => {
                  window.toggleHubspotChat(), setIsOpen(false);
                }}
                label={
                  <Typography
                    classNames="pl-s pr-s"
                    variant={TypographyVariants.p3}
                    weight={TypographyWeights.bold}
                  >
                    {app_main_wpml.start_chat}
                  </Typography>
                }
              />
            </div>
          </div>
        }
      />
    ),
    [isOpen, isWithinTime, assistanceMessage]
  );

  const activeLink = (
    <a
      href={`tel: ${phoneLink}`}
      data-gtm-item={phoneLink}
      onClick={() => {
        if (hasListingPhoneSecureValue) {
          trackRankedPhoneButtonClicked(listingPhoneSecureValue);
        }
      }}
    >
      <i className="insertion-mobile-fixed-footer__icon">
        <Icon icon={phone} className="icon" />
      </i>
    </a>
  );

  const inactiveLink = (
    <>
      {offLineDrawer}
      <div
        onClick={() => {
          if (hasListingPhoneSecureValue) {
            trackRankedPhoneButtonClicked(listingPhoneSecureValue);
          }
          setIsOpen(true);
        }}
      >
        <i className="insertion-mobile-fixed-footer__icon">
          <Icon icon={phone} className="icon" />
        </i>
      </div>
    </>
  );

  useEffect(() => {
    if (!isDeviceSmallerThanTablet) {
      // The pending/expanded chat states are mobile-only; clear leftovers if
      // the viewport crossed the breakpoint mid-session.
      document.body.classList.remove(
        'chat-button-pending',
        'chat-button-expanded'
      );
      return;
    }

    // Neither support button shows until the variant is known: the phone
    // button mounts hidden (hideCucaButton's default) and this class hides
    // the sibling chat widget. setPhoneButtonVisibility — reached by every
    // settle path (flags answered, failsafe timeout, ineligible page, error)
    // — then reveals the right combination directly, so neither cohort sees
    // the other cohort's footer flash first.
    document.body.classList.add('chat-button-pending');

    // CuCa settings take precedence over the PostHog flag: a listing ranked
    // below the CuCa-set phone secure value always hides the button.
    const normalizedCucaPhoneSecureValue =
      normalizePhoneSecureValue(phoneSecureValue);
    const cucaShouldHideButton =
      hasListingPhoneSecureValue &&
      normalizedCucaPhoneSecureValue !== null &&
      normalizedCucaPhoneSecureValue >= normalizedListingPhoneSecureValue;

    const setPhoneButtonVisibility = (flagVariant) => {
      const shouldHide = cucaShouldHideButton || flagVariant === 'test';
      setHideCucaButton(shouldHide);
      document.body.classList.toggle('chat-button-expanded', shouldHide);
      document.body.classList.remove('chat-button-pending');
    };

    const fetchAvailability = async () => {
      const flagVariant =
        window.freedomeConfig.baseLocale === 'es' ? undefined : 'test';

      try {
        const formatted = app_main_wpml.telephone_support_is_available_from_to
          .replace('%FROM%', startTime.slice(0, 5))
          .replace('%TO%', endTime.slice(0, 5));
        setAssistanceMessage(formatted);

        let isWithinSupportHours = isWithinBusinessHours(startTime, endTime);
        setIsWithinTime(isWithinSupportHours);
        setCucaIsAvailable(isWithinSupportHours && Number(isPhoneActive));

        setPhoneButtonVisibility(flagVariant);
      } catch (error) {
        // console.error(error);
        const formatted = app_main_wpml.telephone_support_is_available_from_to
          .replace('%FROM%', DEFAULT_START_TIME.slice(0, 5))
          .replace('%TO%', DEFAULT_END_TIME.slice(0, 5));
        setAssistanceMessage(formatted);

        setIsWithinTime(
          isWithinBusinessHours(DEFAULT_START_TIME, DEFAULT_END_TIME)
        );
        setCucaIsAvailable(false);
        setPhoneButtonVisibility(flagVariant);
      }
    };

    fetchAvailability();
  }, [isDeviceSmallerThanTablet]);

  if (!isDeviceSmallerThanTablet || hideCucaButton) return null;
  if (cucaIsAvailable == true) return activeLink;
  return inactiveLink;
};

export { CucaSupportButton };
